import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { clearAuthData } from '../utils/auth';
import { signOut } from '../utils/logout';
import { looksLikeCardId } from '../utils/cardActivation';
import { useTheme } from '../hooks/useTheme';
import { useIsEnterpriseAdmin } from '../hooks/useIsEnterpriseAdmin';
import { useAccount } from '../contexts/AccountContext';
import homeStyles from '../components/home/HomeHub.module.css';
import { HomeSidebar, HomeTopBar } from '../components/home/HomeHubParts';
import NotificationsDrawer from '../components/home/NotificationsDrawer';
import useHubNotifications from '../hooks/useHubNotifications';
import styles from '../components/account/Account.module.css';
import AvatarField from '../components/account/AvatarField';
import CardIdField from '../components/account/CardIdField';
import AddPhysicalCardDialog from '../components/account/AddPhysicalCardDialog';
import AccountDialog from '../components/account/AccountDialog';
import { InfoRow, NavRow, Notice, Pill, Section, Skeleton } from '../components/account/AccountParts';
import {
  IconBell,
  IconCalendarLink,
  IconCard,
  IconDocument,
  IconExternal,
  IconKey,
  IconMoon,
  IconPlan,
  IconReceipt,
  IconShield,
  IconSignOut,
  IconSparkle,
  IconSun,
  IconTrash,
} from '../components/account/AccountIcons';
import { hasMeaningfulInsightPreferences } from '../utils/insightPreferences';

/** Hidden until light mode is implemented. Flip to true to show the profile appearance switch again. */
const SHOW_APPEARANCE_TOGGLE = false;

function formatLongDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function toDateInputValue(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/**
 * The account hub — the single private destination for who you are and how the
 * app behaves for you.
 *
 * This replaces three overlapping pages (/account, /profile and
 * /account-settings) that each re-implemented name and email editing while
 * disagreeing about where the Card ID, the scheduling link and account
 * deletion lived. The order of the sections is the point: identity, then how
 * you appear publicly, then preferences, then the irreversible things.
 */
export default function AccountPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode, toggleTheme } = useTheme();
  const { isEnterpriseAdmin, isEnterpriseUser } = useIsEnterpriseAdmin();
  const {
    account,
    avatarUrl,
    isLoading: isAccountLoading,
    error: accountError,
    refresh,
    mergeAccount,
    uploadAvatar,
    removeAvatar,
    clearAccount,
  } = useAccount();

  const [isNavOpen, setIsNavOpen] = useState(false);
  const notifications = useHubNotifications();

  const [banner, setBanner] = useState(null); // { tone, message }

  // Identity editing
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [draft, setDraft] = useState({ name: '', email: '', dateOfBirth: '' });
  const [fieldError, setFieldError] = useState('');

  // Insight preferences (status only — editing lives on its own page)
  const [prefs, setPrefs] = useState(null);
  const [isPrefsLoading, setIsPrefsLoading] = useState(true);

  // Scheduling link
  const [schedulingLink, setSchedulingLink] = useState('');
  const [savedSchedulingLink, setSavedSchedulingLink] = useState('');
  const [isSchedulingLoading, setIsSchedulingLoading] = useState(true);
  const [isSavingScheduling, setIsSavingScheduling] = useState(false);
  const [schedulingError, setSchedulingError] = useState('');

  // Dialogs
  const [addCardOpen, setAddCardOpen] = useState(false);
  const [pendingCardId, setPendingCardId] = useState('');
  const [detailsCardId, setDetailsCardId] = useState('');
  const [lostCard, setLostCard] = useState(null);
  const [isMarkingLost, setIsMarkingLost] = useState(false);

  const [signOutOpen, setSignOutOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const name = account?.name || '';
  const email = account?.email || '';
  const physicalCards = (Array.isArray(account?.physicalCards) ? account.physicalCards : [])
    .filter((card) => card.status === 'active' || card.status === 'revoked')
    .slice()
    .sort((a, b) => (a.status === b.status ? 0 : a.status === 'active' ? -1 : 1));
  const isVerified = Boolean(account?.isVerified);
  const prefsSet = useMemo(() => hasMeaningfulInsightPreferences(prefs), [prefs]);

  // A card already activated during this session leaves its ID behind for the
  // account page to adopt — the same handoff the previous account page did.
  useEffect(() => {
    const stored = localStorage.getItem('activationCardId');
    if (stored && physicalCards.length === 0 && looksLikeCardId(stored)) {
      setPendingCardId(stored);
      setAddCardOpen(true);
      localStorage.removeItem('activationCardId');
    }
  }, [physicalCards.length]);

  useEffect(() => {
    setDraft({
      name: account?.name || '',
      email: account?.email || '',
      dateOfBirth: toDateInputValue(account?.dateOfBirth),
    });
  }, [account?.name, account?.email, account?.dateOfBirth]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/user/insight-preferences');
        if (!cancelled) setPrefs(res?.data?.preferences || null);
      } catch {
        if (!cancelled) setPrefs(null);
      } finally {
        if (!cancelled) setIsPrefsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/user/settings');
        if (cancelled) return;
        const link = res?.data?.schedulingLink || '';
        setSchedulingLink(link);
        setSavedSchedulingLink(link);
      } catch {
        /* the field simply starts empty; saving still works */
      } finally {
        if (!cancelled) setIsSchedulingLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleStartEdit = () => {
    setFieldError('');
    setBanner(null);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setFieldError('');
    setDraft({
      name: account?.name || '',
      email: account?.email || '',
      dateOfBirth: toDateInputValue(account?.dateOfBirth),
    });
  };

  const handleSaveIdentity = async (event) => {
    event?.preventDefault();

    const nextName = draft.name.trim();
    const nextEmail = draft.email.trim();

    if (!nextName) {
      setFieldError('Please enter your name.');
      return;
    }
    if (!nextEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextEmail)) {
      setFieldError('Please enter a valid email address.');
      return;
    }

    setIsSaving(true);
    setFieldError('');

    try {
      await api.post('/api/user/updateProfile', {
        name: nextName,
        email: nextEmail,
        dateOfBirth: draft.dateOfBirth || null,
      });

      // The saved profile also feeds the card people already hold, so keep the
      // card's own name in step rather than letting the two drift.
      const businessCard = account?.businessCard || {};
      if (businessCard.name && businessCard.name !== nextName) {
        try {
          await api.post('/api/user/updateBusinessCard', { ...businessCard, name: nextName });
        } catch {
          /* the profile is saved; the card sync is a convenience */
        }
      }

      await refresh();
      setIsEditing(false);
      setBanner({ tone: 'success', message: 'Your details were saved.' });
    } catch (e) {
      const message = e?.response?.data?.error;
      setFieldError(message || 'Could not save your details. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveScheduling = async () => {
    const value = schedulingLink.trim();
    setSchedulingError('');

    if (value && !/^https?:\/\/.+/.test(value)) {
      setSchedulingError('Enter a full URL, including https://');
      return;
    }

    setIsSavingScheduling(true);
    try {
      await api.post('/api/user/settings', { schedulingLink: value || null });
      setSavedSchedulingLink(value);
      setBanner({ tone: 'success', message: value ? 'Scheduling link saved.' : 'Scheduling link removed.' });
    } catch (e) {
      setSchedulingError(e?.response?.data?.error || 'Could not save your scheduling link.');
    } finally {
      setIsSavingScheduling(false);
    }
  };

  const handleCardLinked = (value) => {
    const nextCards = physicalCards.some((card) => card.cardId === value)
      ? physicalCards
      : [{ cardId: value, status: 'active', programmed: false, assignedAt: new Date().toISOString() }, ...physicalCards];
    mergeAccount({ physicalCards: nextCards });
    setPendingCardId('');
    setBanner({ tone: 'success', message: 'Your card is linked.' });
    refresh();
  };

  const handleMarkLost = async () => {
    if (!lostCard) return;
    setIsMarkingLost(true);
    try {
      await api.post(`/api/cards/${encodeURIComponent(lostCard.cardId)}/lost`);
      mergeAccount({
        physicalCards: physicalCards.map((card) => (
          card.cardId === lostCard.cardId ? { ...card, status: 'revoked' } : card
        )),
      });
      setLostCard(null);
      setBanner({ tone: 'success', message: 'That card is marked as lost.' });
      refresh();
    } catch (e) {
      setBanner({ tone: 'error', message: e?.response?.data?.error || 'Could not update that card.' });
    } finally {
      setIsMarkingLost(false);
    }
  };

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await signOut();
    } finally {
      clearAccount();
      setIsSigningOut(false);
      navigate('/login', { replace: true });
    }
  };

  const handleDelete = async () => {
    if (deleteConfirmation.trim().toLowerCase() !== 'delete') {
      setDeleteError('Type "delete" to confirm.');
      return;
    }

    setIsDeleting(true);
    setDeleteError('');
    try {
      await api.delete('/api/user/delete');
      clearAuthData();
      try {
        localStorage.removeItem('verificationPending');
        localStorage.removeItem('activationPromptDismissed');
      } catch {
        /* the token is already gone */
      }
      clearAccount();
      navigate('/login', { replace: true });
    } catch (e) {
      setDeleteError(e?.response?.data?.error || 'Could not delete your account. Please try again.');
      setIsDeleting(false);
    }
  };

  const closeNav = useCallback(() => setIsNavOpen(false), []);

  const schedulingDirty = schedulingLink.trim() !== savedSchedulingLink;

  return (
    <div className={homeStyles.hubRoot}>
      <HomeSidebar
        open={isNavOpen}
        onClose={closeNav}
        activePath={location.pathname}
        onNavigate={(to) => {
          setIsNavOpen(false);
          navigate(to);
        }}
      />

      <NotificationsDrawer
        open={notifications.open}
        onClose={notifications.close}
        {...notifications.drawerProps}
      />

      <div className={homeStyles.hubMain}>
        <HomeTopBar
          title="Account"
          onMenu={() => setIsNavOpen(true)}
          notificationsBadge={notifications.badge}
          onNotifications={notifications.toggle}
        />

        <div className={homeStyles.hubContent}>
          <div className={styles.page}>
            {accountError ? (
              <Notice tone="error">
                {accountError}{' '}
                <button type="button" className={styles.inlineRetry} onClick={refresh}>
                  Try again
                </button>
              </Notice>
            ) : null}

            {banner ? (
              <Notice tone={banner.tone} onDismiss={() => setBanner(null)}>
                {banner.message}
              </Notice>
            ) : null}

            {/* ---------- Identity ---------- */}
            <section className={styles.identity} aria-label="Your profile">
              <AvatarField
                name={name}
                avatarUrl={avatarUrl}
                onUpload={async (dataUrl) => {
                  await uploadAvatar(dataUrl);
                  setBanner({ tone: 'success', message: 'Profile picture updated.' });
                }}
                onRemove={async () => {
                  await removeAvatar();
                  setBanner({ tone: 'success', message: 'Profile picture removed.' });
                }}
              />

              <div className={styles.identityMain}>
                {isAccountLoading && !account ? (
                  <div className={styles.identitySkeleton}>
                    <Skeleton width="54%" height={26} radius={10} />
                    <Skeleton width="42%" height={14} />
                    <Skeleton width="30%" height={22} radius={999} />
                  </div>
                ) : (
                  <>
                    <h1 className={styles.identityName}>{name || 'Your account'}</h1>
                    <p className={styles.identityEmail}>{email || '—'}</p>
                    <div className={styles.identityBadges}>
                      {isVerified ? (
                        <Pill tone="positive">Verified</Pill>
                      ) : (
                        <Pill tone="warning">Email not verified</Pill>
                      )}
                      {account?.role === 'admin' ? <Pill tone="muted">Admin</Pill> : null}
                      {isEnterpriseAdmin ? <Pill tone="muted">Enterprise Admin</Pill> : null}
                      {isEnterpriseUser ? <Pill tone="muted">Enterprise User</Pill> : null}
                    </div>
                  </>
                )}
              </div>

              {!isEditing ? (
                <div className={styles.identityActions}>
                  <button
                    type="button"
                    className={styles.secondaryButton}
                    onClick={handleStartEdit}
                    disabled={!account}
                  >
                    Edit details
                  </button>
                </div>
              ) : null}
            </section>

            {isEditing ? (
              <form className={styles.editCard} onSubmit={handleSaveIdentity} noValidate>
                <div className={styles.editGrid}>
                  <div className={styles.field}>
                    <label className={styles.fieldLabel} htmlFor="account-name">
                      Full name
                    </label>
                    <input
                      id="account-name"
                      className={styles.input}
                      value={draft.name}
                      onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))}
                      autoComplete="name"
                      required
                    />
                  </div>

                  <div className={styles.field}>
                    <label className={styles.fieldLabel} htmlFor="account-email">
                      Email
                    </label>
                    <input
                      id="account-email"
                      className={styles.input}
                      type="email"
                      value={draft.email}
                      onChange={(e) => setDraft((p) => ({ ...p, email: e.target.value }))}
                      autoComplete="email"
                      required
                    />
                    <p className={styles.fieldHint}>Used to sign in and to reach you about your account.</p>
                  </div>

                  <div className={styles.field}>
                    <label className={styles.fieldLabel} htmlFor="account-dob">
                      Date of birth
                    </label>
                    <input
                      id="account-dob"
                      className={styles.input}
                      type="date"
                      value={draft.dateOfBirth}
                      onChange={(e) => setDraft((p) => ({ ...p, dateOfBirth: e.target.value }))}
                      autoComplete="bday"
                    />
                    <p className={styles.fieldHint}>Never shown on your card or shared with contacts.</p>
                  </div>
                </div>

                {fieldError ? (
                  <p className={styles.formError} role="alert">
                    {fieldError}
                  </p>
                ) : null}

                <div className={styles.editActions}>
                  <button
                    type="button"
                    className={styles.secondaryButton}
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                  <button type="submit" className={styles.primaryButton} disabled={isSaving}>
                    {isSaving ? 'Saving…' : 'Save changes'}
                  </button>
                </div>
              </form>
            ) : (
              /* Name and email are already in the header above, so these rows
                 carry only what it does not show. */
              <div className={styles.detailsCard}>
                <InfoRow label="Date of birth" value={formatLongDate(account?.dateOfBirth)} empty="Not set" />
                <InfoRow label="Member since" value={formatLongDate(account?.joined)} empty="—" />
              </div>
            )}

            <div className={styles.grid}>
              {/* ---------- Public presentation ---------- */}
              <Section
                id="card"
                eyebrow="Profile"
                title="Your profile"
                description="What people see from your link, QR code, or a physical card."
              >
                <div className={styles.rowGroup}>
                  <NavRow icon={<IconCard />} label="Edit card details" hint="Name, company, role and links" to="/my-card" />
                  {account?._id ? (
                    <NavRow
                      icon={<IconExternal />}
                      label="Preview public card"
                      hint="Exactly what a new contact sees"
                      to={`/c/${account._id}`}
                    />
                  ) : null}
                </div>

                <div className={styles.physicalBlock}>
                  <h3 className={styles.physicalHeading}>Physical cards</h3>
                  {physicalCards.length === 0 ? (
                    <button type="button" className={styles.physicalEmpty} onClick={() => { setPendingCardId(''); setAddCardOpen(true); }}>
                      <span className={styles.physicalEmptyMark} aria-hidden="true">+</span>
                      <span className={styles.physicalEmptyTitle}>Add a physical card</span>
                      <span className={styles.physicalEmptyText}>Link one you already have or order a new one.</span>
                    </button>
                  ) : (
                    <div className={styles.physicalList}>
                      {physicalCards.map((card) => {
                        const lost = card.status === 'revoked';
                        return (
                          <article className={styles.physicalRow} key={card.cardId}>
                            <div className={styles.physicalHead}>
                              <p className={styles.physicalName}>VELOX Card</p>
                              <Pill tone={lost ? 'warning' : 'positive'}>{lost ? 'Lost' : 'Active'}</Pill>
                            </div>
                            <p className={styles.physicalMeta}>
                              {lost ? 'No longer opens your profile' : 'Opens your profile'}
                              {card.assignedAt ? ` · Linked ${formatLongDate(card.assignedAt)}` : ''}
                            </p>
                            <div className={styles.physicalActions}>
                              <button
                                type="button"
                                className={styles.textButton}
                                aria-expanded={detailsCardId === card.cardId}
                                onClick={() => setDetailsCardId((current) => (current === card.cardId ? '' : card.cardId))}
                              >
                                Card details
                              </button>
                              {!lost ? (
                                <button type="button" className={styles.textButton} onClick={() => setLostCard(card)}>
                                  Mark as lost
                                </button>
                              ) : null}
                            </div>
                            {detailsCardId === card.cardId ? <CardIdField cardId={card.cardId} /> : null}
                          </article>
                        );
                      })}
                    </div>
                  )}
                  {physicalCards.length > 0 ? (
                    <button
                      type="button"
                      className={`${styles.physicalEmpty} ${styles.physicalEmptyAnother}`}
                      onClick={() => { setPendingCardId(''); setAddCardOpen(true); }}
                    >
                      <span className={styles.physicalEmptyMark} aria-hidden="true">+</span>
                      <span className={styles.physicalEmptyTitle}>
                        {physicalCards.some((card) => card.status === 'active') ? 'Add another card' : 'Add a physical card'}
                      </span>
                      <span className={styles.physicalEmptyText}>Link one you already have or order a new one.</span>
                    </button>
                  ) : null}
                </div>
              </Section>

              {/* ---------- Preferences ---------- */}
              <Section
                id="plan"
                eyebrow="Plan"
                title={isEnterpriseUser ? 'Your plan' : 'Plans and billing'}
                description={
                  isEnterpriseUser
                    ? 'What your company seat includes.'
                    : isEnterpriseAdmin
                      ? 'Team seats, cards and billing for the company.'
                      : 'What your account includes, and how cards are ordered.'
                }
              >
                <div className={styles.rowGroup}>
                  <NavRow
                    icon={<IconPlan />}
                    label="Plans"
                    hint={
                      isEnterpriseUser
                        ? 'What your seat includes'
                        : isEnterpriseAdmin
                          ? 'Team seats and card pricing'
                          : 'Compare Basic, Pro and Teams'
                    }
                    to="/upgrade"
                  />
                  {!isEnterpriseUser ? (
                    <NavRow
                      icon={<IconReceipt />}
                      label="Billing"
                      hint="Payment method and invoices"
                      to="/billing"
                    />
                  ) : null}
                </div>
              </Section>

              <Section
                id="preferences"
                eyebrow="Preferences"
                title="How Velox works for you"
                description={SHOW_APPEARANCE_TOGGLE ? 'Content, alerts and appearance.' : 'Content and alerts.'}
              >
                <div className={styles.rowGroup}>
                  <NavRow
                    icon={<IconSparkle />}
                    label="Insight preferences"
                    hint="Preferences behind your Market Intelligence and networking recommendations."
                    to="/insight-preferences"
                    status={
                      isPrefsLoading ? (
                        <Skeleton width={64} height={20} radius={999} />
                      ) : prefsSet ? (
                        <Pill tone="positive">Set</Pill>
                      ) : (
                        <Pill tone="warning">Not set</Pill>
                      )
                    }
                  />
                  <NavRow
                    icon={<IconBell />}
                    label="Notifications"
                    hint="Connection invitation emails"
                    to="/notification-settings"
                  />
                </div>

                {SHOW_APPEARANCE_TOGGLE ? (
                  <div className={styles.row}>
                    <span className={styles.rowIcon} aria-hidden="true">
                      {isDarkMode ? <IconMoon /> : <IconSun />}
                    </span>
                    <span className={styles.rowMain}>
                      <span className={styles.rowLabel}>Appearance</span>
                      <span className={styles.rowHint}>{isDarkMode ? 'Dark' : 'Light'} — synced to your account</span>
                    </span>
                    <button
                      type="button"
                      className={styles.switch}
                      role="switch"
                      aria-checked={isDarkMode}
                      onClick={toggleTheme}
                      aria-label="Use dark appearance"
                    >
                      <span className={styles.switchThumb} aria-hidden="true" />
                    </button>
                  </div>
                ) : null}

                <div className={styles.field}>
                  <label className={styles.fieldLabel} htmlFor="account-scheduling">
                    <span className={styles.fieldLabelIcon} aria-hidden="true">
                      <IconCalendarLink size={15} />
                    </span>
                    Scheduling link
                  </label>
                  <div className={styles.inlineForm}>
                    <input
                      id="account-scheduling"
                      className={styles.input}
                      type="url"
                      inputMode="url"
                      placeholder="https://calendly.com/your-link"
                      value={schedulingLink}
                      onChange={(e) => {
                        setSchedulingLink(e.target.value);
                        setSchedulingError('');
                      }}
                      disabled={isSchedulingLoading}
                    />
                    <button
                      type="button"
                      className={styles.secondaryButton}
                      onClick={handleSaveScheduling}
                      disabled={isSavingScheduling || isSchedulingLoading || !schedulingDirty}
                    >
                      {isSavingScheduling ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                  {schedulingError ? (
                    <p className={styles.formError} role="alert">
                      {schedulingError}
                    </p>
                  ) : (
                    <p className={styles.fieldHint}>
                      Offered as “share my booking link” when you arrange a meeting with a contact.
                    </p>
                  )}
                </div>
              </Section>

              {/* ---------- Security ---------- */}
              <Section
                id="security"
                eyebrow="Security"
                title="Security &amp; privacy"
                description="Your password, what you share, and ending your session."
              >
                <div className={styles.rowGroup}>
                  <NavRow
                    icon={<IconKey />}
                    label="Change password"
                    hint="We email you a secure reset link"
                    onClick={() => navigate('/forgot-password', { state: { fromAccount: true } })}
                  />
                  <NavRow
                    icon={<IconShield />}
                    label="Privacy"
                    hint="Location sharing when you make a new contact"
                    to="/privacy-settings"
                  />
                  <NavRow
                    icon={<IconDocument />}
                    label="Legal documents"
                    hint="Privacy policy, terms and cookies"
                    to="/policy"
                  />
                </div>

                <div className={styles.rowGroup}>
                  <NavRow icon={<IconSignOut />} label="Sign out" onClick={() => setSignOutOpen(true)} />
                </div>
              </Section>

              {/* ---------- Irreversible ---------- */}
              <Section
                id="danger"
                eyebrow="Careful"
                title="Delete account"
                description="Permanently removes your account, your contacts and your card data. This cannot be undone."
                tone="danger"
              >
                <button
                  type="button"
                  className={styles.dangerButton}
                  onClick={() => {
                    setDeleteConfirmation('');
                    setDeleteError('');
                    setDeleteOpen(true);
                  }}
                >
                  <IconTrash size={16} />
                  Delete my account
                </button>
              </Section>
            </div>
          </div>
        </div>
      </div>

      {/* ---------- Dialogs ---------- */}
      <AddPhysicalCardDialog
        open={addCardOpen}
        initialCardId={pendingCardId}
        onClose={() => {
          setAddCardOpen(false);
          setPendingCardId('');
        }}
        onLinked={handleCardLinked}
      />

      <AccountDialog
        open={Boolean(lostCard)}
        onClose={() => { if (!isMarkingLost) setLostCard(null); }}
        title="Mark this card as lost?"
        lead="This will disable this physical card so it can no longer open your profile. This cannot be undone."
        tone="danger"
        footer={
          <>
            <button type="button" className={styles.secondaryButton} onClick={() => setLostCard(null)} disabled={isMarkingLost}>
              Cancel
            </button>
            <button type="button" className={styles.dangerButton} onClick={handleMarkLost} disabled={isMarkingLost}>
              {isMarkingLost ? 'Updating…' : 'Mark as lost'}
            </button>
          </>
        }
      />

      <AccountDialog
        open={signOutOpen}
        onClose={() => setSignOutOpen(false)}
        title="Sign out of Velox?"
        lead="You will need your email and password to sign back in on this device."
        footer={
          <>
            <button type="button" className={styles.secondaryButton} onClick={() => setSignOutOpen(false)}>
              Stay signed in
            </button>
            <button type="button" className={styles.primaryButton} onClick={handleSignOut} disabled={isSigningOut}>
              {isSigningOut ? 'Signing out…' : 'Sign out'}
            </button>
          </>
        }
      />

      <AccountDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Delete your account"
        lead="This permanently deletes your account, your saved contacts and your card data. It cannot be undone."
        tone="danger"
        footer={
          <>
            <button type="button" className={styles.secondaryButton} onClick={() => setDeleteOpen(false)} disabled={isDeleting}>
              Keep my account
            </button>
            <button
              type="button"
              className={styles.dangerButton}
              onClick={handleDelete}
              disabled={isDeleting || deleteConfirmation.trim().toLowerCase() !== 'delete'}
            >
              {isDeleting ? 'Deleting…' : 'Delete account'}
            </button>
          </>
        }
      >
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="delete-confirm">
            Type <strong>delete</strong> to confirm
          </label>
          <input
            id="delete-confirm"
            className={styles.input}
            value={deleteConfirmation}
            onChange={(e) => {
              setDeleteConfirmation(e.target.value);
              setDeleteError('');
            }}
            autoComplete="off"
            spellCheck="false"
          />
          {deleteError ? (
            <p className={styles.formError} role="alert">
              {deleteError}
            </p>
          ) : null}
        </div>
      </AccountDialog>
    </div>
  );
}
